/**
 * @Description 数字输入框
 *
 * 外卖占比禁用状态控制特殊处理prd
 * https://confluence.lanhanba.com/pages/viewpage.action?pageId=149533541
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { Form, Tooltip } from 'antd';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import { isNotEmpty, contrast, isArray, deepCopy, isUndef, floorKeep } from '@lhb/func';
import { ControlType, estimateSalesReasonableSelectYHT, parseValueCatch, setComValue } from '../config';
import { customEstimateSalesKWKOfBasicUsableArea, customEstimateSalesKWKOfNumberFields, customEstimateSalesKWKOfSpecWideLocation } from '@/common/components/business/ExpandStore/ts-config';
import {
  estimatedDailyIdentification,
  passengerFlowAverageIdentification,
  takeawayfactorIdentification
} from '../customize';
import { PASSENGER_FLOW_WEEKEND } from './PassengerFlow/config';
import IconFont from '@/common/components/IconFont';
import styles from './index.module.less';
import { useMethods } from '@lhb/hook';

export interface NumberInputProps {
  propertyItem: any;
  disabled: boolean;
  required: boolean;
  showHintStr?: Function;
  salesFactorOfKWK?: any;
  setSalesFactorOfKWK?: any;
  form: any;
  tiledFormDataRef: any;
}

const NumberInput: FC<NumberInputProps> = ({
  propertyItem,
  disabled,
  required,
  showHintStr,
  salesFactorOfKWK,
  setSalesFactorOfKWK,
  form,
  tiledFormDataRef,
}) => {
  const [customizeDisabled, setCustomizeDisabled] = useState<boolean>(false); // 外卖占比禁用状态控制特殊处理
  const [helpInfo, setHelpInfo] = useState<string>('');
  const label = propertyItem.anotherName || propertyItem.name || '';
  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};
  const templateRestriction = propertyItem.templateRestriction ? JSON.parse(propertyItem.templateRestriction)
    : {};
  // const defaultValue = templateRestriction.defaultValue;// 默认值
  const minRange = templateRestriction.minRange;// 模板配置-最小值
  const maxRange = templateRestriction.maxRange;// 模板配置-最大值
  const placeholder = restriction.placeholder ? restriction.placeholder : `请输入${label || ''}`;
  const minValue = isUndef(minRange) ? contrast(restriction, 'range.minValue') : minRange; // 如果模板没配置最小值会返回null，则取属性上
  const maxValue = isUndef(maxRange) ? contrast(restriction, 'range.maxValue', 9999999999) : maxRange;// 如果模板没配置最大值会返回null，则取属性上
  const decimals = contrast(restriction, 'precision.decimals', 0); // 小数点位数
  const addonAfter = Array.isArray(restriction.suffixOptionList) && restriction.suffixOptionList.length && restriction.suffixOptionList[0].name ? restriction.suffixOptionList[0].name : null;

  const passengerFlowWeekend = Form.useWatch(PASSENGER_FLOW_WEEKEND.key, form); // 客流计数-周末客流

  const rules = [
    { required, message: placeholder },
    () => ({
      validator(_, value) {
        if (isNotEmpty(minValue) && isNotEmpty(value) && value < minValue) {
          return Promise.reject(`请输入至少${minValue}`);
        }
        return Promise.resolve();
      },
    }),
  ];

  useEffect(() => {
    methods.updatePassengerFlowAverage();
  }, [passengerFlowWeekend]);

  useEffect(() => {
    if (!propertyItem?.textValue) return;
    const textValue = parseValueCatch(propertyItem);
    changeHandle(textValue?.value);
  }, [propertyItem]);

  // 营业额预估相关特殊处理 start
  const esrYHTValue = Form.useWatch(estimateSalesReasonableSelectYHT.keyValue, form);// 营业额预估合理值的选中值

  // 营业额预估合理值选中项改变时、控制外卖系数字段的禁用
  useEffect(() => {
    if (propertyItem?.identification === takeawayfactorIdentification) {
      // 外卖占比字段的特殊处理：预估结论值为系统预估的选项，需要将本输入框组件设置为禁用
      const isSystemEstimate = checkEsrSystemEstimate(esrYHTValue);
      setCustomizeDisabled(isSystemEstimate);
    }
  }, [esrYHTValue]);

  /**
   * @description 检查益禾堂预估结论合理值是否为系统预估
   * @param esrValue 益禾堂预估结论合理值，为其他属性的idenfication，字符串
   * @return true 属于系统预估；false 手动输入
   */
  const checkEsrSystemEstimate = (esrValue: string) => {
    const identifications: any = [estimatedDailyIdentification];

    const limitYHTCom = tiledFormDataRef?.current?.find((item) => item.controlType === ControlType.ESTIMATE_SALES_TWO_YHT.value);
    if (limitYHTCom?.identification) {
      identifications.push(limitYHTCom.identification);
    }
    return identifications.includes(esrValue);
  };
  // 营业额预估相关特殊处理 end

  const isDisabled = useMemo(() => {
    if (!propertyItem) return false;
    const { restriction, identification } = propertyItem;
    if (identification === 'returnperiod') return true; // 定制逻辑（益禾堂租户专用的字段）
    if (!restriction) return false;
    const { expr } = parseValueCatch(propertyItem, 'restriction');
    return isArray(expr) && expr.length > 0;
  }, [propertyItem]);

  const changeHandle = (val: number) => {
    const { restriction } = propertyItem || {};
    // 区分是数据回显还是主动触发
    if (salesFactorOfKWK && customEstimateSalesKWKOfNumberFields.includes(propertyItem?.identification)) {
      if (isNotEmpty(val)) { // 有输入值
        let targetVal = 0;
        if (propertyItem.identification === customEstimateSalesKWKOfNumberFields[0]) {
          targetVal = customEstimateSalesKWKOfBasicUsableArea(val);
        }
        if (propertyItem.identification === customEstimateSalesKWKOfNumberFields[1]) {
          targetVal = customEstimateSalesKWKOfSpecWideLocation(val);
        }
        setSalesFactorOfKWK((state) => ({ ...state, [propertyItem.identification]: targetVal }));
      } else { // 清空
        const _copyValue = deepCopy(salesFactorOfKWK);
        Reflect.deleteProperty(_copyValue, propertyItem.identification);
        setSalesFactorOfKWK && setSalesFactorOfKWK(_copyValue);
      }
    }
    setHelpInfo('');
    if (!restriction) return;
    const parsedRestriction = JSON.parse(restriction);
    const { redMark } = parsedRestriction || {};
    if (!redMark) return;
    const {
      firstComapre,
      firstValue,
      isItemTip,
      isRedMark,
      itemTip,
      joinType,
      secondComapre,
      secondValue,
      isPageTip
    } = redMark;
    // if (!joinType) return; // 值为 '&&'或者'||'
    let expressionStr = '';
    if (firstComapre && firstValue) { // 满足的第一个条件
      expressionStr += `(${val}${firstComapre === '=' ? '==' : firstComapre}${firstValue})`;
    }
    if (secondComapre && secondValue) { // 满足的第二个条件
      // 如果第一个条件 ？(拼接上joinType + 第二个条件) : 第二个条件
      expressionStr += `${expressionStr ? `${firstComapre && firstValue && joinType ? joinType : ''}(${val}${secondComapre === '=' ? '==' : secondComapre}${secondValue})` : `${val}${secondComapre}${secondValue}`}`;
    }
    // eslint-disable-next-line no-eval
    if (eval(expressionStr)) {
      isRedMark && isItemTip && itemTip && (setHelpInfo(itemTip));
      isPageTip && showHintStr && (showHintStr());
    }
  };

  const renderLabel = () => {
    if (propertyItem?.identification === passengerFlowAverageIdentification) {
      return (
        <div className={styles.numberInputTitle}>
          {label}
          <Tooltip title='日均客流=（周末客流*60%*22+周末客流*8）/30'>
            <div>
              <IconFont className={styles.titleIcon} iconHref='pc-common-icon-ic_info' />
            </div>
          </Tooltip>
        </div>
      );
    }
    return label;
  };

  const methods = useMethods({
    /** 同步更新日均客流 */
    updatePassengerFlowAverage() {
      if (propertyItem?.identification === passengerFlowAverageIdentification) {

        let value: any = null;
        if (!isUndef(passengerFlowWeekend)) {
          value = passengerFlowWeekend === 0 ? 0 : floorKeep(passengerFlowWeekend * 0.6 * 22 + passengerFlowWeekend * 8, 30, 4, 0);
        }

        form.setFieldsValue({ [passengerFlowAverageIdentification]: value });
        setComValue(propertyItem, value);
      }
    }
  });

  return (
    <V2FormInputNumber
      label={renderLabel()}
      name={propertyItem.identification}
      placeholder={placeholder}
      max={maxValue}
      min={minValue}
      precision={!!decimals && decimals > 0 ? decimals : 0}
      config={{ addonAfter }}
      required={required}
      rules={rules}
      disabled={isDisabled || disabled || customizeDisabled}
      onChange={changeHandle}
      formItemConfig={{
        help: helpInfo ? <div className='c-f23'>{helpInfo}</div> : null
      }}
    />
  );
};

export default NumberInput;
